import React, { useEffect, useState } from "react"
import { Stack } from "@mui/material"
import { Form, Formik } from "formik"
import PackagingExecutionSectionsForm from "./PackagingExecutionSectionsForm"
import PackagingExecutionPackagings from "./PackagingExecutionPackagings"
import { formatPackagingExecutionWeightsInitialValues, packagingExecutionWeightValidator } from "../../../utils/packagingExecutions"
import { getPseWithSectionOutputs } from "../../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"

const getPackagingNumberByBrand = (packagingNumber = [], brand = "FOODCHERI") => {
	const currentPackagingNumber = packagingNumber.find(item => item.brand === brand)
	return currentPackagingNumber ? currentPackagingNumber.value : 1
}

export const packagingExecutionWeightsFormId = "PackagingExecutionWeightsForm"

const PackagingExecutionWeightsForm = ({ packagingExecution, handleSubmit, editable = true, proposedWeightsBySections }) => {
	const [sectionOutputScales, setSectionOutputScales] = useState(null)
	const [sectionOutputNames, setSectionOutputNames] = useState(null)

	useEffect(() => {
		(async () => {
			const scales = {}
			const names = {}
			for (const section of packagingExecution.sections) {
				const sectionObjectId = section.section?.objectId || section.section?.id
				const outputs = section.section?.sectionOutputs
				if (!outputs || outputs.length === 0) {
					scales[sectionObjectId] = 1
					continue
				}
				// Calculate what percentage of this section stays here (not consumed by other sections)
				const results = await Promise.all(outputs.map(async output => {
					const pse = await getPseWithSectionOutputs([output], null, packagingExecution.recipe?.objectId || packagingExecution.recipe?.id)
					if (pse) return { percentage: 0, name: null } // consumed elsewhere
					const totalNetWeight = outputs.reduce((acc, o) => acc + (o.ingredients ?? []).reduce((acc, i) => acc + i.netWeight, 0), 0)
					const percentage = output.percentage
						? output.percentage
						: (output.ingredients.reduce((acc, i) => acc + i.netWeight, 0) / totalNetWeight * 100)
					return { percentage, name: output.name ?? `${Math.round(percentage)} %` }
				}))
				const totalPercentage = results.reduce((acc, r) => acc + r.percentage, 0)
				scales[sectionObjectId] = totalPercentage > 0 ? totalPercentage / 100 : 1
				const remainingNames = results.filter(r => r.name).map(r => r.name).join(", ")
				if (remainingNames) names[sectionObjectId] = remainingNames
			}
			setSectionOutputScales(scales)
			setSectionOutputNames(names)
		})()
	}, [packagingExecution, proposedWeightsBySections])

	if (!sectionOutputScales) return null

	return (
		<Formik
			initialValues={formatPackagingExecutionWeightsInitialValues(packagingExecution, proposedWeightsBySections, sectionOutputScales, sectionOutputNames)}
			onSubmit={handleSubmit}
			validationSchema={packagingExecutionWeightValidator}
		>
			{({ values, setFieldValue, touched, setFieldTouched, errors }) => {
				return (
					<Form id={packagingExecutionWeightsFormId}>
						<Stack spacing={4}>
							<Stack direction="row">
								<PackagingExecutionPackagings
									packagings={values.packagings}
									expectedPackagingNumber={values.expectedPackagingNumber}
									packagingForecastNumber={values.packagingForecastNumber}
									setFieldValue={setFieldValue}
								/>
							</Stack>
							<Stack direction="row">
								<PackagingExecutionSectionsForm
									editable={editable}
									sections={values.sections}
									packagingForecastNumber={values.packagingForecastNumber}
									touchedSections={touched.sections}
									setFieldValue={setFieldValue}
									setFieldTouched={setFieldTouched}
									FCPackagingNumber={getPackagingNumberByBrand(packagingExecution.packagingNumber)}
								/>
							</Stack>
						</Stack>
						{(errors && errors.sections) && (
							<div style={{ width: "100%", display: "flex", color: "red", marginTop: 8, justifyContent: "end", fontSize: 16 }}>
								{Array.isArray(errors.sections) && errors.sections.map((err, idx) =>
									err && <div key={`section-error-${idx}`}>{Object.values(err).join(", ")}</div>
								)}
							</div>
						)}
					</Form>
				)
			}}
		</Formik>
	)
}

export default PackagingExecutionWeightsForm